<template>
  <TeleportWithDirection to="body">
    <div class="fixed inset-0 bg-black/20 dark:bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
      <div class="bg-white dark:bg-slate-800 rounded-xl w-full max-w-4xl max-h-[90vh] border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
        <!-- Header with Steps -->
        <div class="p-6 border-b border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
              {{ $t('BULK_BROADCAST.TITLE') }}
            </h3>
            <button
              @click="$emit('close')"
              class="p-1 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <Icon icon="i-lucide-x" size="20" />
            </button>
          </div>

          <!-- Step Indicators -->
          <div class="flex items-center space-x-4 overflow-x-auto pb-1">
            <div
              v-for="(step, index) in steps"
              :key="step.key"
              class="flex items-center flex-shrink-0"
            >
              <div
                :class="[
                  'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                  index < currentStep
                    ? 'bg-green-600 dark:bg-green-600 text-white'
                    : index === currentStep
                    ? 'bg-blue-600 dark:bg-blue-500 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                ]"
              >
                <Icon
                  v-if="index < currentStep"
                  icon="i-lucide-check"
                  size="16"
                />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                :class="[
                  'ml-2 text-sm font-medium',
                  index === currentStep
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400'
                ]"
              >
                {{ step.title }}
              </span>
              <Icon
                v-if="index < steps.length - 1"
                icon="i-lucide-chevron-right"
                class="mx-3 text-slate-400"
                size="16"
              />
            </div>
          </div>
        </div>

        <!-- Step Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Step 1: Channel Selection -->
          <BulkChannelSelector
            v-if="currentStep === 0"
            v-model:selected-channel="wizardData.channel"
            v-model:selected-inbox="wizardData.selectedInbox"
            @update:selected-channel="handleChannelChange"
            @update:selected-inbox="handleInboxChange"
          />
          
          <!-- Campaign Name Input - After channel selection -->
          <div 
            v-if="currentStep === 0 && wizardData.channel"
            class="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700"
          >
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">
              {{ $t('BULK_BROADCAST.CAMPAIGN_NAME.LABEL') }}
            </label>
            <input
              v-model="wizardData.campaignName"
              type="text"
              :placeholder="$t('BULK_BROADCAST.CAMPAIGN_NAME.PLACEHOLDER')"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BULK_BROADCAST.CAMPAIGN_NAME.HELP_TEXT') }}
            </p>

            <!-- Programar envío -->
            <div class="mt-6 flex items-start justify-between gap-4">
              <div class="min-w-0">
                <h6 class="text-sm font-medium text-slate-900 dark:text-white">
                  {{ $t('BULK_BROADCAST.SCHEDULE.SWITCH_LABEL') }}
                </h6>
                <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {{ $t('BULK_BROADCAST.SCHEDULE.SWITCH_HELP') }}
                </p>
              </div>
              <Switch v-model="wizardData.scheduleEnabled" class="mt-1 flex-shrink-0" />
            </div>
          </div>

          <!-- Step 2: Template Selection (WhatsApp only) -->
          <WhatsAppTemplateSelector
            v-if="currentStep === 1 && wizardData.channel === 'whatsapp'"
            v-model:selected-template="wizardData.selectedTemplate"
            v-model:multimedia-url="wizardData.multimediaUrl"
            :selected-inbox="wizardData.selectedInbox"
            @update:selected-template="handleTemplateChange"
            @update:multimedia-url="handleMultimediaUrlChange"
          />

          <!-- Step 3: CSV Upload -->
          <BulkCsvUpload
            v-if="currentStep === getStepIndex('csv') && wizardData.selectedTemplate"
            :csv-data="wizardData.csvRows"
            :csv-errors="wizardData.csvErrors"
            :required-variables="requiredVariables"
            @update:csv-data="handleCsvDataUpdate"
            @update:csv-errors="handleCsvErrorsUpdate"
          />
          
          <!-- Error state for CSV step without template -->
          <div v-if="currentStep === getStepIndex('csv') && !wizardData.selectedTemplate" class="text-center py-12">
            <Icon icon="i-lucide-alert-triangle" class="text-amber-400 mb-3" size="48" />
            <h6 class="font-medium text-slate-900 dark:text-white mb-2">
              {{ $t('BULK_BROADCAST.NO_TEMPLATE_SELECTED.TITLE') }}
            </h6>
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ $t('BULK_BROADCAST.NO_TEMPLATE_SELECTED.DESCRIPTION') }}
            </p>
          </div>

          <!-- Step 4: Assignment Selection -->
          <BulkAssignmentSelector
            v-if="currentStep === getStepIndex('assignment')"
            v-model="wizardData.assignment"
            :selected-inbox="wizardData.selectedInbox"
          />

          <!-- Step 5: Schedule (solo si el envío es programado) -->
          <BulkScheduler
            v-if="currentStep === getStepIndex('schedule')"
            v-model="wizardData.scheduledAt"
            :campaign-name="wizardData.campaignName"
            :total-contacts="wizardData.preview.rows.length"
            :template="wizardData.selectedTemplate"
          />

          <!-- Step 6: Preview -->
          <BulkPreview
            v-if="currentStep === getStepIndex('preview')"
            :preview-data="wizardData.preview"
            :channel="wizardData.channel"
            :template="wizardData.selectedTemplate"
            :assignment="wizardData.assignment"
            @update:preview="handlePreviewUpdate"
          />
        </div>

        <!-- Navigation -->
        <div class="p-6 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <button
            v-if="currentStep > 0"
            @click="previousStep"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            {{ $t('BULK_BROADCAST.PREVIOUS') }}
          </button>
          <div v-else></div>

          <div class="flex items-center space-x-3">
            <button
              @click="$emit('close')"
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {{ $t('BULK_BROADCAST.CANCEL') }}
            </button>

            <button
              v-if="!isLastStep"
              @click="nextStep"
              :disabled="!canProceedToNext"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $t('BULK_BROADCAST.NEXT') }}
            </button>

            <button
              v-else
              @click="submitBroadcast"
              :disabled="!canSubmit || isSubmitting"
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Icon
                v-if="isSubmitting"
                icon="i-lucide-loader-2"
                class="animate-spin"
                size="16"
              />
              <span>{{ submitLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>

<script setup>
import { ref, computed, reactive, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import bulkBroadcastService from 'dashboard/services/bulkBroadcast';

import BulkChannelSelector from './BulkChannelSelector.vue';
import WhatsAppTemplateSelector from './WhatsAppTemplateSelector.vue';
import BulkCsvUpload from './BulkCsvUpload.vue';
import BulkAssignmentSelector from './BulkAssignmentSelector.vue';
import BulkScheduler from './BulkScheduler.vue';
import BulkPreview from './BulkPreview.vue';

const { t } = useI18n();
const emit = defineEmits(['close', 'submit-bulk']);

const currentStep = ref(0);
const isSubmitting = ref(false);

const wizardData = reactive({
  mode: 'bulk',
  channel: null,
  selectedInbox: null,
  selectedTemplate: null,
  multimediaUrl: '',
  campaignName: '',
  scheduleEnabled: false,
  scheduledAt: '',
  csvRaw: null,
  csvRows: [],
  csvErrors: [],
  mapping: {
    phone: 'telefonos',
    name: 'nombres',
    vars: ['variable_1', 'variable_2', 'variable_3', 'variable_4', 'variable_5']
  },
  preview: {
    rows: []
  },
  assignment: {
    teamId: 'keep_current',
    agentId: 'keep_current'
  }
});

const steps = computed(() => {
  const baseSteps = [
    { key: 'channel', title: t('BULK_BROADCAST.STEPS.CHANNEL') }
  ];

  if (wizardData.channel === 'whatsapp') {
    baseSteps.push({ key: 'template', title: t('BULK_BROADCAST.STEPS.TEMPLATE') });
  }

  baseSteps.push(
    { key: 'csv', title: t('BULK_BROADCAST.STEPS.CSV') },
    { key: 'assignment', title: t('BULK_BROADCAST.STEPS.ASSIGNMENT') }
  );

  if (wizardData.scheduleEnabled) {
    baseSteps.push({ key: 'schedule', title: t('BULK_BROADCAST.STEPS.SCHEDULE') });
  }

  baseSteps.push({ key: 'preview', title: t('BULK_BROADCAST.STEPS.PREVIEW') });

  return baseSteps;
});

const getStepIndex = (stepKey) => {
  return steps.value.findIndex(step => step.key === stepKey);
};

const submitLabel = computed(() =>
  wizardData.scheduleEnabled
    ? t('BULK_BROADCAST.SCHEDULE.SUBMIT')
    : t('BULK_BROADCAST.SEND')
);

const isLastStep = computed(() => {
  return currentStep.value === steps.value.length - 1;
});

const requiredVariables = computed(() => {
  if (!wizardData.selectedTemplate || !wizardData.selectedTemplate.variables) {
    return 0;
  }
  return Array.isArray(wizardData.selectedTemplate.variables) 
    ? wizardData.selectedTemplate.variables.length 
    : 0;
});

const canProceedToNext = computed(() => {
  const step = steps.value[currentStep.value];
  
  switch (step.key) {
    case 'channel':
      return wizardData.channel && (wizardData.channel !== 'whatsapp' || wizardData.selectedInbox);
    case 'template': {
      // Check if template is selected
      if (!wizardData.selectedTemplate) return false;
      
      // Check if template has multimedia and URL is provided
      const hasMultimedia = wizardData.selectedTemplate.rawTemplate?.components?.some(comp => 
        comp.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)
      );
      
      // If has multimedia, URL is required
      if (hasMultimedia && !wizardData.multimediaUrl) return false;
      
      return true;
    }
    case 'csv':
      return wizardData.csvRows && wizardData.csvRows.length > 0 && 
             wizardData.csvErrors && wizardData.csvErrors.length === 0;
    case 'schedule':
      return isScheduleValid.value;
    default:
      return true;
  }
});

// La fecha se valida acá además de en BulkScheduler para no depender del componente
// hijo: bloquea "Siguiente" y el submit final.
const isScheduleValid = computed(() => {
  if (!wizardData.scheduleEnabled) return true;
  if (!wizardData.scheduledAt) return false;

  const scheduled = new Date(wizardData.scheduledAt);
  if (Number.isNaN(scheduled.getTime()) || scheduled.getTime() <= Date.now()) return false;

  // Tope de un año, igual que BulkBroadcast::MAX_SCHEDULE_AHEAD en el backend.
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() + 1);
  return scheduled.getTime() <= limit.getTime();
});

const canSubmit = computed(() => {
  return wizardData.csvRows && wizardData.csvRows.length > 0 && 
         wizardData.csvErrors && wizardData.csvErrors.length === 0 && 
         wizardData.preview && wizardData.preview.rows && wizardData.preview.rows.length > 0 &&
         isScheduleValid.value;
});

watch(() => wizardData.scheduleEnabled, (enabled) => {
  if (!enabled) {
    wizardData.scheduledAt = '';
  }
});

const handleChannelChange = (channel) => {
  wizardData.channel = channel;
  // Reset dependent data when channel changes
  wizardData.selectedInbox = null;
  wizardData.selectedTemplate = null;
  wizardData.csvRows = [];
  wizardData.csvErrors = [];
  wizardData.preview.rows = [];
};

const handleInboxChange = (inbox) => {
  wizardData.selectedInbox = inbox;
  // Reset template and CSV when inbox changes
  wizardData.selectedTemplate = null;
  wizardData.csvRows = [];
  wizardData.csvErrors = [];
  wizardData.preview.rows = [];
};

const handleTemplateChange = (template) => {
  wizardData.selectedTemplate = template;
  // Reset CSV and preview when template changes
  wizardData.csvRows = [];
  wizardData.csvErrors = [];
  wizardData.preview.rows = [];
  // Keep multimedia URL when template changes (user can modify it)
};

const handleMultimediaUrlChange = (url) => {
  wizardData.multimediaUrl = url;
};

const handleCsvDataUpdate = (data) => {
  wizardData.csvRows = data;
  if (data && data.length > 0 && wizardData.selectedTemplate) {
    generatePreview();
  } else {
    wizardData.preview.rows = [];
  }
};

const handleCsvErrorsUpdate = (errors) => {
  wizardData.csvErrors = errors;
};

const handleCsvChange = (data) => {
  try {
    // Ensure data exists and has the expected structure
    if (!data) {
      console.warn('No data received in handleCsvChange');
      return;
    }
    
    wizardData.csvRows = Array.isArray(data.rows) ? data.rows : [];
    wizardData.csvErrors = Array.isArray(data.errors) ? data.errors : [];
    
    // Only generate preview if we have rows and a selected template
    if (wizardData.csvRows.length > 0 && wizardData.selectedTemplate) {
      generatePreview();
    } else {
      wizardData.preview.rows = [];
    }
  } catch (error) {
    console.error('Error processing CSV:', error);
    wizardData.csvErrors = ['Error al procesar el archivo CSV'];
    wizardData.preview.rows = [];
  }
};

const handlePreviewUpdate = (preview) => {
  wizardData.preview = preview;
};

const generatePreview = () => {
  try {
    // Validate required data
    if (!Array.isArray(wizardData.csvRows) || wizardData.csvRows.length === 0) {
      wizardData.preview.rows = [];
      return;
    }
    
    if (!wizardData.selectedTemplate) {
      console.warn('No template selected for preview generation');
      wizardData.preview.rows = [];
      return;
    }

    wizardData.preview.rows = wizardData.csvRows.map((row, index) => {
      const phone = row?.telefonos || '';
      const name = row?.nombres || '';
      
      let renderedMessage = '';
      const variablesUsed = [];

      if (wizardData.channel === 'whatsapp' && wizardData.selectedTemplate) {
        renderedMessage = wizardData.selectedTemplate.body || '';
        
        // Replace variables in order {{1}}, {{2}}, etc.
        if (Array.isArray(wizardData.selectedTemplate.variables)) {
          wizardData.selectedTemplate.variables.forEach((variableInfo, index) => {
            const placeholder = `{{${index + 1}}}`;
            const columnName = `variable_${index + 1}`;
            const value = row?.[columnName] || '';
            
            // Replace all occurrences of this placeholder
            renderedMessage = renderedMessage.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
            
            if (value) {
              variablesUsed.push(value);
            }
          });
        } else {
          // Fallback: try to replace common variable patterns
          for (let i = 1; i <= 5; i++) {
            const placeholder = `{{${i}}}`;
            const columnName = `variable_${i}`;
            const value = row?.[columnName] || '';
            
            if (renderedMessage.includes(placeholder) && value) {
              renderedMessage = renderedMessage.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
              variablesUsed.push(value);
            }
          }
        }
      }

      return {
        phone,
        name,
        renderedMessage: renderedMessage || (wizardData.selectedTemplate?.body || ''),
        variablesUsed,
        templateId: wizardData.selectedTemplate?.id
      };
    });
  } catch (error) {
    console.error('Error generating preview:', error);
    wizardData.preview.rows = [];
    wizardData.csvErrors = [...(wizardData.csvErrors || []), 'Error al generar la vista previa: ' + error.message];
  }
};

const nextStep = () => {
  if (canProceedToNext.value && currentStep.value < steps.value.length - 1) {
    currentStep.value++;
  }
};

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
};

// Códigos estables del backend → mensaje traducido. Sin esto el usuario ve el
// texto crudo en inglés que devuelve la API.
const SCHEDULE_ERROR_KEYS = {
  scheduled_at_in_past: 'BULK_BROADCAST.SCHEDULE.ERRORS.PAST',
  scheduled_at_invalid: 'BULK_BROADCAST.SCHEDULE.ERRORS.INVALID',
  scheduled_at_too_far: 'BULK_BROADCAST.SCHEDULE.ERRORS.TOO_FAR'
};

const submitBroadcast = async () => {
  if (!canSubmit.value) return;

  // Revalidación al momento de enviar: el wizard puede haber quedado abierto hasta
  // que la fecha elegida quedó en el pasado.
  if (wizardData.scheduleEnabled && !isScheduleValid.value) {
    useAlert(t('BULK_BROADCAST.SCHEDULE.ERRORS.PAST'), { usei18n: false });
    currentStep.value = getStepIndex('schedule');
    return;
  }

  isSubmitting.value = true;
  
  try {
    // Convertir valores especiales al payload
    const getAssignmentValue = (value) => {
      if (value === 'keep_current') return undefined; // No enviar el campo
      if (value === null) return null; // Enviar explícitamente null para desasignar
      return value; // Enviar el ID numérico
    };
    
    const payload = wizardData.preview.rows.map(row => {
      const data = {
        inbox_id: wizardData.selectedInbox?.id,
        template_id: wizardData.selectedTemplate?.id || wizardData.selectedTemplate?.name,
        language: wizardData.selectedTemplate?.language,
        to: row.phone,
        name: row.name,
        variables: row.variablesUsed,
        campaign_name: wizardData.campaignName || ''
      };
      
      // Include multimedia URL if template has multimedia
      if (wizardData.multimediaUrl) {
        data.header_handle = wizardData.multimediaUrl;
      }
      
      // Solo incluir team_id si no es 'keep_current'
      const teamValue = getAssignmentValue(wizardData.assignment.teamId);
      if (teamValue !== undefined) {
        data.team_id = teamValue;
      }
      
      // Solo incluir assignee_id si no es 'keep_current'
      const agentValue = getAssignmentValue(wizardData.assignment.agentId);
      if (agentValue !== undefined) {
        data.assignee_id = agentValue;
      }
      
      return data;
    });

    // DEBUG: Uncomment for production debugging
    // Validate payload before sending
    const validation = bulkBroadcastService.validatePayload(wizardData.channel, payload);
    if (!validation.isValid) {
      useAlert(`Errores de validación: ${validation.errors.join(', ')}`, { usei18n: false });
      return;
    }

    // El input datetime-local está en hora local; se manda en ISO/UTC para que el
    // servidor lo interprete sin ambigüedad.
    const scheduledAt = wizardData.scheduleEnabled && wizardData.scheduledAt
      ? new Date(wizardData.scheduledAt).toISOString()
      : null;

    // Send the bulk messages
    const response = await bulkBroadcastService.sendBulkMessages(wizardData.channel, payload, { scheduledAt });
    
    // Show success message based on response
    if (response.scheduled) {
      const label = new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeStyle: 'short' })
        .format(new Date(response.scheduled_at));
      useAlert(`Envío masivo programado para el ${label}. ${response.total_messages} mensajes en espera. Se creó la tarea en el calendario.`, { usei18n: false });
    } else if (response.async) {
      useAlert(`Envío masivo iniciado. ${response.total_messages} mensajes en cola de procesamiento.`, { usei18n: false });
    } else {
      useAlert(`Envío completado: ${response.successful} exitosos, ${response.failed} fallidos.`, { usei18n: false });
    }
    
    emit('submit-bulk', { payload, response });
    
  } catch (error) {
    console.error('Error submitting bulk broadcast:', error);
    
    // Log the detailed error response
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);
      console.error('Error headers:', error.response.headers);
    }
    
    let errorMessage = 'Error desconocido en el envío masivo';
    
    if (error.response && error.response.data) {
      const errorCode = error.response.data.code;
      if (SCHEDULE_ERROR_KEYS[errorCode]) {
        useAlert(t(SCHEDULE_ERROR_KEYS[errorCode]), { usei18n: false });
        currentStep.value = getStepIndex('schedule');
        return;
      }

      if (typeof error.response.data === 'string') {
        // If it's HTML error page, extract the title or show generic message
        if (error.response.data.includes('<title>')) {
          errorMessage = 'Error interno del servidor (500)';
        } else {
          errorMessage = error.response.data;
        }
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    useAlert(`Error en el envío: ${errorMessage}`, { usei18n: false });
  } finally {
    isSubmitting.value = false;
  }
};

// Watchers para actualizar automáticamente la previsualización
watch(() => wizardData.selectedTemplate, () => {
  if (wizardData.selectedTemplate && wizardData.csvRows.length > 0) {
    nextTick(() => {
      generatePreview();
    });
  }
}, { deep: true });

watch(() => wizardData.csvRows, () => {
  if (wizardData.selectedTemplate && wizardData.csvRows.length > 0) {
    nextTick(() => {
      generatePreview();
    });
  }
}, { deep: true });
</script>